/**
 * Prescription Form Component
 *
 * Available on all plans. Enter prescription medications
 * to receive ingredient conflict warnings in product recommendations and diet plans.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Plus, X, Pill, Shield } from 'lucide-react';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { useToast } from './ui/Toast';

const Container = styled(Card)`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const Title = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
`;

const PrescriptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PrescriptionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.light};
`;

const PrescriptionName = styled.div`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text.tertiary};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const WarningCard = styled(Card)`
  padding: 1rem;
  background: ${theme.colors.status.warning}10;
  border: 1px solid ${theme.colors.status.warning}30;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const WarningText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  line-height: 1.5;
`;

const InfoCard = styled(Card)`
  padding: 1rem;
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const InfoText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.primary[700]};
  line-height: 1.5;
`;

/**
 * Common prescription medications that might interact with skincare ingredients
 */
const PRESCRIPTION_DATABASE = [
  'tretinoin', 'retin-a', 'retinoid', 'accutane', 'isotretinoin',
  'adapalene', 'differin', 'tazarotene', 'tazorac',
  'benzoyl peroxide', 'clindamycin', 'erythromycin',
  'spironolactone', 'birth control', 'oral contraceptive',
  'antibiotic', 'doxycycline', 'minocycline', 'tetracycline',
  'hydroquinone', 'cortisone', 'steroid', 'prednisone',
];

/**
 * Check for ingredient conflicts with prescriptions
 */
const checkIngredientConflicts = (prescriptions: string[], ingredient: string): string[] => {
  const conflicts: string[] = [];
  const lowerIngredient = ingredient.toLowerCase();

  prescriptions.forEach(prescription => {
    const lowerPrescription = prescription.toLowerCase();

    // Retinoid conflicts
    if (lowerPrescription.includes('retinoid') || lowerPrescription.includes('tretinoin') || 
        lowerPrescription.includes('retin-a') || lowerPrescription.includes('accutane')) {
      if (lowerIngredient.includes('retinol') || lowerIngredient.includes('retinoid') || 
          lowerIngredient.includes('vitamin a') || lowerIngredient.includes('retinyl')) {
        conflicts.push(`${prescription} + Retinol/Vitamin A: May cause excessive dryness and irritation`);
      }
      if (lowerIngredient.includes('aha') || lowerIngredient.includes('glycolic') || 
          lowerIngredient.includes('lactic acid') || lowerIngredient.includes('salicylic')) {
        conflicts.push(`${prescription} + AHAs/BHAs: May cause excessive exfoliation and irritation`);
      }
    }

    // Antibiotic conflicts
    if (lowerPrescription.includes('antibiotic') || lowerPrescription.includes('doxycycline') || 
        lowerPrescription.includes('minocycline')) {
      if (lowerIngredient.includes('benzoyl peroxide')) {
        conflicts.push(`${prescription} + Benzoyl Peroxide: May reduce antibiotic effectiveness`);
      }
    }

    // Hydroquinone conflicts
    if (lowerPrescription.includes('hydroquinone')) {
      if (lowerIngredient.includes('retinol') || lowerIngredient.includes('vitamin c') || 
          lowerIngredient.includes('ascorbic acid')) {
        conflicts.push(`${prescription} + Retinol/Vitamin C: May cause skin irritation`);
      }
    }
  });

  return conflicts;
};

export const PrescriptionForm: React.FC = () => {
  const { userProfile, updateUserProfile } = useAWSAuth();
  const { showToast } = useToast();
  const [prescriptions, setPrescriptions] = useState<string[]>(
    (userProfile as any)?.prescriptions || []
  );
  const [newPrescription, setNewPrescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddPrescription = () => {
    if (!newPrescription.trim()) return;

    const trimmed = newPrescription.trim();
    if (prescriptions.includes(trimmed)) {
      showToast('This prescription is already in your list', 'info');
      return;
    }

    setPrescriptions(prev => [...prev, trimmed]);
    setNewPrescription('');
    showToast('Prescription added', 'success');
  };

  const handleRemovePrescription = (index: number) => {
    setPrescriptions(prev => prev.filter((_, i) => i !== index));
    showToast('Prescription removed', 'success');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateUserProfile({ prescriptions } as any);
      showToast('Prescriptions saved successfully', 'success');
    } catch (error) {
      console.error('Error saving prescriptions:', error);
      showToast('Failed to save prescriptions', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <Shield size={24} color={theme.colors.status.warning} />
        <div>
          <Title>Prescription Medications</Title>
          <Description>
            Enter your prescription medications to receive ingredient conflict warnings in product recommendations and diet plans.
          </Description>
        </div>
      </Header>

      <FormGroup>
        <Label>Add Prescription</Label>
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
          <Input
            type="text"
            placeholder="e.g., Tretinoin, Accutane, Spironolactone..."
            value={newPrescription}
            onChange={(e) => setNewPrescription(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddPrescription();
              }
            }}
            style={{ flex: 1 }}
          />
          <Button
            variant="primary"
            onClick={handleAddPrescription}
            disabled={!newPrescription.trim()}
            style={{ flexShrink: 0 }}
          >
            <Plus size={18} />
          </Button>
        </div>
      </FormGroup>

      {prescriptions.length > 0 && (
        <>
          <FormGroup>
            <Label>Your Prescriptions ({prescriptions.length})</Label>
            <PrescriptionList>
              {prescriptions.map((prescription, index) => (
                <PrescriptionItem key={index}>
                  <PrescriptionName>
                    <Pill size={18} color={theme.colors.primary[500]} />
                    {prescription}
                  </PrescriptionName>
                  <RemoveButton onClick={() => handleRemovePrescription(index)}>
                    <X />
                  </RemoveButton>
                </PrescriptionItem>
              ))}
            </PrescriptionList>
          </FormGroup>

          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{ alignSelf: 'flex-start' }}
          >
            {isSaving ? 'Saving...' : 'Save Prescriptions'}
          </Button>
        </>
      )}

      {prescriptions.length === 0 && (
        <InfoCard>
          <Shield size={20} color={theme.colors.primary[500]} />
          <InfoText>
            <strong>Why add prescriptions?</strong><br />
            GlowLens will check all product recommendations and diet suggestions for potential conflicts with your medications. 
            This helps keep you safe and ensures your skincare routine works well with your prescriptions.
          </InfoText>
        </InfoCard>
      )}
    </Container>
  );
};

/**
 * Check if a product ingredient conflicts with user's prescriptions
 */
export const checkProductConflicts = (
  productIngredients: string[],
  userPrescriptions: string[]
): string[] => {
  const allConflicts: string[] = [];
  
  productIngredients.forEach(ingredient => {
    const conflicts = checkIngredientConflicts(userPrescriptions, ingredient);
    allConflicts.push(...conflicts);
  });

  return allConflicts;
};

/**
 * Check if a food item conflicts with user's prescriptions
 */
export const checkFoodConflicts = (
  foodName: string,
  foodIngredients: string[],
  userPrescriptions: string[]
): string[] => {
  const allConflicts: string[] = [];
  const allText = `${foodName} ${foodIngredients.join(' ')}`;
  
  const conflicts = checkIngredientConflicts(userPrescriptions, allText);
  allConflicts.push(...conflicts);

  return allConflicts;
};



